import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import {
    OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID,
    OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID,
    OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID,
    OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID
} from "@constants/oidc-login-hint";
import { LoginHintSecurityMethodTypes } from "@enums/login-hint-security-methode-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { GeneralAdvancedValidationErrors } from "@idw-types/common/validation/common/general-advanced-validation-errors";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn/axn-documentation-link";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { LoginHintContainer } from "@containers/common/properties-sections/login-hint-container/login-hint-container";

type VerificationServiceLoginHintContainerProps = {
    isDisabled: boolean;
    validationErrors: GeneralAdvancedValidationErrors;
    documentationLinks: Array<AxnDocumentationLink>;
    rpServiceConfiguration: FormattedRpServiceConfiguration;
    updateProperties: (properties: Array<{
        propertyTypeId: string;
        newValue: string;
        added: boolean;
    }>) => void;
};

export const VerificationServiceLoginHintContainer = (props: VerificationServiceLoginHintContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        documentationLinks,
        rpServiceConfiguration,
        updateProperties
    } = props;

    const onChangeEnableLoginHint = (newValue: string) => {
        if (isStringTrue(newValue)) {
            updateProperties([{ propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: "true", added: true }]);
        } else if (rpServiceConfiguration.workflowID) {
            updateProperties([
                { propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: "false", added: true },
                { propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
            ]);
        } else {
            updateProperties([
                { propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
            ]);
        }
    };

    const onChangeLoginHintSecurityMethod = (newValue: LoginHintSecurityMethodTypes) => {
        const propertiesToUpdate: Array<{ propertyTypeId: string; newValue: string; added: boolean; }> = [];
        if (newValue === LoginHintSecurityMethodTypes.PLAIN_JSON) {
            propertiesToUpdate.push(...[
                { propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: "true", added: true },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
            ]);

            if (rpServiceConfiguration.workflowID) {
                propertiesToUpdate.push({ propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: "false", added: true });
            } else {
                propertiesToUpdate.push({ propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: null, added: false });
            }
        } else if (newValue === LoginHintSecurityMethodTypes.SIGNED_JWT) {
            propertiesToUpdate.push(...[
                { propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: "true", added: true },
                { propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: "true", added: true }
            ]);

            if (rpServiceConfiguration.workflowID) {
                propertiesToUpdate.push(...[
                    { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: null, added: true },
                    { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: null, added: true }
                ]);
            } else {
                propertiesToUpdate.push(...[
                    { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: null, added: false },
                    { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: null, added: false }
                ]);
            }
        } else if (newValue === LoginHintSecurityMethodTypes.JWT_ENCRYPTED) {
            propertiesToUpdate.push(...[
                { propertyTypeId: OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID, newValue: "true", added: true },
                { propertyTypeId: OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID, newValue: "true", added: true },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID, newValue: "", added: true },
                { propertyTypeId: OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID, newValue: "", added: true }
            ]);
        }

        updateProperties(propertiesToUpdate);
    };

    return (
        <LoginHintContainer
            isDisabled={isDisabled}
            validationErrors={validationErrors}
            documentationLinks={documentationLinks}
            appliedProfiles={rpServiceConfiguration.appliedProfiles}
            properties={rpServiceConfiguration.properties}
            pageLevel={PageLevelTypes.APIKEY}
            canModifyNonRestrictedProperties={rpServiceConfiguration.permissions.canModifyNonRestrictedProperties}
            workflowId={rpServiceConfiguration.workflowID}
            onChangeEnableLoginHint={onChangeEnableLoginHint}
            onChangeLoginHintSecurityMethod={onChangeLoginHintSecurityMethod}
            updateProperties={updateProperties}
        />
    );
};